<script setup lang="ts">
import { onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import debounce from "lodash-es/debounce";

import { baseInput, baseTextarea } from "@/common/components";

import { MethodItemPayloadType } from "@/common/types/method/method-item";

import { FieldType } from "@/common/types/field";

const { t } = useI18n();

const emit = defineEmits(["update-data"]);

const props = withDefaults(
  defineProps<{
    data: MethodItemPayloadType | null;
    fields: FieldType[] | null;
    title?: string;
    width?: string;
    order?: number;
  }>(),
  {
    width: "100%",
  },
);

const DEFAULT_PAYLOAD = {
  code: "",
  name: "",
  metadata: null,
};

const methodPayload = ref<MethodItemPayloadType>({ ...DEFAULT_PAYLOAD });
const metadataPayload = ref<FieldType[]>([]);

function update() {
  emit("update-data", methodPayload.value);
}

async function fetchFields() {
  if (!props.fields) return;

  await new Promise((resolve) => {
    metadataPayload.value =
      props.fields?.reduce((acc: FieldType[], field) => {
        if (field.type === "RANGE") {
          acc.push({
            ...field,
            answer: "",
            measure: "",
            min: "",
            max: "",
          });
        } else {
          acc.push({
            ...field,
            answer: "",
          });
        }
        return acc;
      }, []) ?? [];

    resolve(true);
  });
}

function fetchMetadata() {
  if (!props.data?.metadata) return;

  const metadata: FieldType[] = props.data.metadata;

  metadata?.forEach((field) => {
    metadataPayload.value?.map((item) => {
      if (item.field_id === field.field_id) {
        item.answer = field.answer;

        if (field.type === "RANGE") {
          item.measure = field.measure;
          item.min = field.min;
          item.max = field.max;
        }
      }

      return item;
    });
  });
}

function updateField() {
  methodPayload.value.metadata = metadataPayload.value;

  update();
}

const debouncedUpdate = debounce(update, 500);
const debouncedUpdateField = debounce(updateField, 500);

watch(
  () => props.fields,
  async () => {
    await fetchFields();
    fetchMetadata();
  },
);

watch(
  () => props.data,
  () => {
    fetchMetadata();
  },
);

onMounted(async () => {
  await fetchFields();

  if (props.data) {
    methodPayload.value = { ...props.data };

    fetchMetadata();
  }
});
</script>

<template>
  <section class="form-content">
    <div v-if="title" class="title">
      {{ title }}
    </div>
    <form>
      <base-input
        v-model="methodPayload.code"
        :value="methodPayload.code"
        :placeholder="t('common.placeholders.code')"
        @input="debouncedUpdate()"
      >
        <template #label>
          {{ t("common.labels.code") }}
        </template>
      </base-input>

      <base-input
        v-model="methodPayload.name"
        :value="methodPayload.name"
        :placeholder="t('common.placeholders.name')"
        @input="debouncedUpdate()"
      >
        <template #label>
          {{ t("common.labels.name") }}
        </template>
      </base-input>

      <template v-for="(item, index) in metadataPayload" :key="index">
        <template v-if="item.type === 'TEXT'">
          <base-input
            v-model="item.answer"
            :value="item.answer"
            @input="debouncedUpdateField()"
          >
            <template #label>
              {{ item.name }}
            </template>
          </base-input>
        </template>

        <template v-if="item.type === 'NUMBER'">
          <base-input
            v-model="item.answer"
            :value="item.answer"
            type="number"
            @input="debouncedUpdateField()"
          >
            <template #label>
              {{ item.name }}
            </template>
          </base-input>
        </template>

        <template v-if="item.type === 'RANGE'">
          <base-input
            v-model="item.measure"
            :value="item?.measure"
            placeholder="ex: kg"
            @input="debouncedUpdateField()"
          >
            <template #label>
              {{ item.name }}
              {{ t("question.labels.measure") }}
            </template>
          </base-input>

          <base-input
            v-model="item.min"
            :value="item?.min"
            type="number"
            placeholder="ex: 0.01"
            @input="debouncedUpdateField()"
          >
            <template #label>
              {{ item.name }}
              {{ t("question.labels.minimum") }}
            </template>
          </base-input>

          <base-input
            v-model="item.max"
            :value="item?.max"
            type="number"
            placeholder="ex: 0.2"
            @input="debouncedUpdateField()"
          >
            <template #label>
              {{ item.name }}
              {{ t("question.labels.maximum") }}
            </template>
          </base-input>
        </template>

        <template v-if="item.type === 'TEXTAREA'">
          <base-textarea
            v-model="item.answer"
            :value="item.answer"
            @input="debouncedUpdateField()"
          >
            <template #label>
              {{ item.name }}
            </template>
          </base-textarea>
        </template>
      </template>
    </form>
  </section>
</template>

<style lang="scss" scoped>
$width: v-bind("width");

.form-content {
  display: flex;
  flex-direction: column;
  width: calc($width - 0.5rem - 1rem);
  padding-inline-start: 0.5rem;
  padding-inline-end: 1rem;
  gap: 1.25rem;

  .title {
    display: inline-flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    font-size: 1.15rem;
    font-weight: bold;
    text-transform: uppercase;
    border-block-end: 1px solid var(--border);
    padding-block-end: 0.5rem;
    padding-inline-start: 0.5rem;
    padding-inline-end: 1rem;
  }

  .separate {
    height: 1.25rem;
  }

  form {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .options {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding-block-start: 0.5rem;
    padding-block-end: 1.5rem;

    .item {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 0.75rem;
      padding-inline-start: 0.5rem;
      padding-inline-end: 1rem;
    }
  }
}
</style>
